/**
 * Die Hauptnavigation der AppShell — als eigene Datei, damit sie prüfbar ist.
 *
 * P7.1 verlangt nicht nur „es gibt einen Eintrag Auswertung", sondern seine **Stelle**: Antoines
 * Festlegung vom 30.07.2026 lautet „Menüpunkt Auswertung steht zwischen Aufgaben und Katalog".
 * Eine Reihenfolge, die niemand prüft, verrutscht beim nächsten Eintrag. Aus `AppShell.tsx`
 * heraus wäre sie nur mit dem ganzen Layout-Gerüst zu prüfen (Stack Auth, Copilot, Suche) —
 * hier ist sie eine Liste ohne jede Abhängigkeit.
 *
 * P0 (docs/KONZEPT-KATALOG-NAVIGATION.md § 4.1): „Katalog" bekommt die Untereinträge „Karten"
 * und „Pakete". Anlass war Antoines Frage vom 31.07.2026 — *„Wo kann ich Karten archivieren?"*.
 * Das Archivieren gab es längst, nur an einem Ort, den die Seitenleiste nicht nannte. Der Import
 * bleibt bewusst OHNE Eintrag: er ist eine Handlung, die von „Karten" ausgeht (§ 4.1).
 *
 * Nav-Umbau 28.08.2026 (Antoine: „Die Navi ist ein durcheinander. Wie kann man diese besser
 * strukturieren."): bis dahin standen 13 flache Top-Level-Einträge ohne jede visuelle
 * Gruppierung nebeneinander (einzige Ausnahme „Katalog", s.o.) — UND die mobile Fußleiste zeigte
 * nur 5 von 16 erreichbaren Zielen, der Rest war am Telefon gar nicht erreichbar. Zwei
 * unabhängige Maßnahmen, beide OHNE die bestehende Reihenfolge/P7.1-Festlegung anzufassen:
 *  (a) `gruppe` — ein rein visuelles Feld, das benachbarte Einträge zu Clustern zusammenfasst
 *      (`NavListe.tsx::SeitenNavigation` rendert eine Überschrift bei jedem Wechsel). Es
 *      VERSCHIEBT keinen Eintrag — die P7.1-Ratsche (`test/auswertung-seite.test.tsx`: „Katalog"
 *      unmittelbar nach „Auswertung") bleibt unberührt, weil die Array-Reihenfolge exakt
 *      dieselbe bleibt, nur mit Zwischenüberschriften versehen.
 *  (b) `MobileNavDrawer` (`NavListe.tsx`) — ein Vollbild-Ausklapp-Menü fürs Telefon, das
 *      DIESELBE gruppierte Liste wie die Desktop-Seitenleiste zeigt (inkl. „Katalog"-Kinder),
 *      per Hamburger-Knopf in der mobilen Topbar erreichbar. `MOBILE_NAV` (die 5-Wege-Fußleiste)
 *      bleibt UNVERÄNDERT bestehen (schneller Zugriff auf die meistgenutzten Wege) — der Drawer
 *      ergänzt volle Erreichbarkeit, statt die Fußleiste zu ersetzen.
 */
export interface NavEintrag {
  /**
   * Klickziel. Fehlt bei einer Gruppe: „Katalog" ist laut § 8 (Red Team) nur die Überschrift
   * ihrer Untereinträge und ausdrücklich NICHT klickbar — sonst wäre unklar, wo man landet.
   */
  to?: string;
  /**
   * Pfad-Präfix des Bereichs für den Topbar-Titel. Bei einem Blatt gleich `to`; bei einer Gruppe
   * der gemeinsame Stamm, damit `/katalog/import` weiterhin „Katalog" über sich stehen hat.
   */
  bereich?: string;
  label: string;
  /** Material-Symbol-Name. */
  icon: string;
  /** Nur für „/": exakte Übereinstimmung, sonst wäre der Eintrag immer aktiv. */
  end?: boolean;
  /** Untereinträge. Sind welche da, ist dieser Eintrag eine Gruppe (kein Link). */
  kinder?: NavEintrag[];
  /**
   * Rein visuelles Cluster-Label (Nav-Umbau 28.08.2026) — verändert NICHT die Reihenfolge/
   * Erreichbarkeit, nur die Darstellung: ein Wechsel des Werts zum Vorgänger-Eintrag löst in
   * `SeitenNavigation`/`MobileNavDrawer` eine neue Zwischenüberschrift aus. „Katalog" selbst
   * braucht keins (hat schon seine eigene Überschrift über den `kinder`-Mechanismus).
   */
  gruppe?: string;
}

export const NAV: NavEintrag[] = [
  { to: "/", label: "Übersicht", icon: "dashboard", end: true },
  { to: "/anfragen", label: "Anfragen", icon: "inbox", gruppe: "Vertrieb" },
  // Posteingang-UI (Omnichannel-Clearing-House, 26.08.2026): eigenes Icon, "inbox" ist von
  // "Anfragen" belegt (strukturierte Lead-Aufnahme, ein anderes Konzept als rohe Nachrichten).
  // Position bewusst hier - weit weg von der geschuetzten Auswertung->Katalog-Nachbarschaft
  // (P7.1, test/auswertung-seite.test.tsx).
  { to: "/posteingang", label: "Posteingang", icon: "forum", gruppe: "Vertrieb" },
  // F0 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md, Antoine D2 12.09.2026): "Veranstaltungen"
  // verschwindet aus dem Backoffice - "Buchungen" sind alle Faelle ab Zusage/Zahlung.
  // Punkt 118 (Antoine 24.09.2026): alle Angebote ueber alle Kunden - direkt OBERHALB von
  // "Buchungen" (Reihenfolge geprueft in test/angebote-seite.test.tsx).
  { to: "/angebote", label: "Angebote", icon: "request_quote", gruppe: "Vertrieb" },
  { to: "/buchungen", label: "Buchungen", icon: "event_available", gruppe: "Vertrieb" },
  { to: "/kalender", label: "Kalender", icon: "calendar_month", gruppe: "Vertrieb" },
  { to: "/kunden", label: "Kunden", icon: "group", gruppe: "Vertrieb" },
  { to: "/aufgaben", label: "Aufgaben", icon: "checklist", gruppe: "Planung" },
  // P7.1: Antoines Festlegung 30.07.2026 — „Menüpunkt Auswertung steht zwischen Aufgaben und
  // Katalog" (geprueft in test/auswertung-seite.test.tsx: „Katalog" muss der UNMITTELBARE
  // Nachfolger von „Auswertung" bleiben) — ein neuer Eintrag darf deshalb NICHT dazwischen.
  { to: "/auswertung", label: "Auswertung", icon: "query_stats", gruppe: "Planung" },
  // P0 (KONZEPT-KATALOG-NAVIGATION § 4.1): zwei Untereinträge, weil es zwei verschiedene Fragen
  // sind — „Karten" ist Bestandspflege, „Pakete" ist Verkaufsvorbereitung (§ 3).
  {
    label: "Katalog",
    icon: "menu_book",
    bereich: "/katalog",
    kinder: [
      { to: "/katalog/karten", bereich: "/katalog/karten", label: "Karten", icon: "restaurant_menu" },
      { to: "/katalog/pakete", bereich: "/katalog/pakete", label: "Pakete", icon: "inventory_2" },
    ],
  },
  // Punkt 124 (Antoine, 26.09.2026): „Business Health“ entfällt ersatzlos — aufgegangen in
  // „Auswertung“ unter Planung (keine Umleitung). Test: test/auswertung-seite.test.tsx.
  // Lern-Übersicht 25.09.2026: „Wissen“ heißt „Gedächtnis“ (/wissen leitet um, ui/umleitungen.tsx).
  { to: "/gedaechtnis", label: "Gedächtnis", icon: "psychology", gruppe: "Einblicke" },
  { to: "/zahlungen", label: "Zahlungen", icon: "payments", gruppe: "Verwaltung" },
  // F2.1: ohne diesen Eintrag gibt es die Freigabeliste zwar, aber niemand kommt an sie heran —
  // und die Erinnerungen, die der Sweep vorlegt, gingen nie hinaus. Die Stelle ist NACH
  // „Zahlungen" gewaehlt, weil „Auswertung" zwischen „Aufgaben" und „Katalog" stehen muss
  // (Antoines Festlegung 30.07.2026, geprueft in test/auswertung-seite.test.tsx).
  { to: "/freigaben", label: "Freigaben", icon: "outgoing_mail", gruppe: "Verwaltung" },
  // Mail-Protokoll (Antoine, 26.08.2026: "Wo können wir das Protokoll aller versendeten E-Mails
  // einsehen?"). Eigenes Icon ("outgoing_mail" ist von "Freigaben" belegt) — hier NACH
  // "Freigaben", weil beide denselben Versandweg betreffen (was hinaus WILL vs. was hinaus GING).
  { to: "/mail-protokoll", label: "Mail-Protokoll", icon: "history_edu", gruppe: "Verwaltung" },
  // C-03-Hinweis (aufgaben.md, WELLE 1): Backoffice-Sicht auf `unmatched_provider_events` — Stripe-
  // Webhook-Ereignisse ohne lokalen Treffer, bis hierher nur in der DB sichtbar. Direkt NACH
  // „Mail-Protokoll" (letzter Eintrag der Gruppe „Verwaltung" vor „Einstellungen"): thematisch das
  // Gegenstück zu „Zahlungen" (ungeklärte statt normale Vorgänge), aber eine eigene, oben stehende
  // Nachbarschaft (Zahlungen -> Freigaben) mit einer festen Ratsche (mail-protokoll-seite.test.tsx:
  // „Mail-Protokoll direkt nach Freigaben") wird dafür NICHT aufgebrochen.
  { to: "/ungeklaerte-zahlungen", label: "Ungeklärte Zahlungen", icon: "sync_problem", gruppe: "Verwaltung" },
  { to: "/einstellungen", label: "Einstellungen", icon: "settings", gruppe: "Verwaltung" },
];

/** Am Telefon in der Fußleiste nur die fünf meistgenutzten Wege — der Hamburger-Knopf in der
 *  Topbar (`AppShell.tsx`) öffnet `MobileNavDrawer` mit ALLEN 17 Zielen, siehe Kopfkommentar
 *  Nav-Umbau 28.08.2026. */
export const MOBILE_NAV: NavEintrag[] = NAV.filter(
  (n) => n.to != null && ["/", "/anfragen", "/buchungen", "/kalender", "/einstellungen"].includes(n.to),
);

/**
 * Alle anklickbaren Ziele in Reihenfolge — Gruppen selbst sind keine (§ 8). Wer wissen will, ob
 * ein Weg erreichbar ist, fragt diese Liste, nicht `NAV`.
 */
export const NAV_ZIELE: NavEintrag[] = NAV.flatMap((n) => (n.kinder ? n.kinder : n.to != null ? [n] : []));

/**
 * Der Bereichstitel für die Topbar. Der SPEZIFISCHSTE Treffer gewinnt: `/katalog/karten` heißt
 * „Karten", `/katalog/import` fällt auf die Gruppe „Katalog" zurück.
 */
export function aktiverTitel(pfad: string): string {
  if (pfad === "/") return "Übersicht";
  let treffer: { praefix: string; label: string } | null = null;
  const pruefe = (eintrag: NavEintrag) => {
    const praefix = eintrag.bereich ?? eintrag.to;
    if (praefix == null || praefix === "/" || !pfad.startsWith(praefix)) return;
    if (!treffer || praefix.length > treffer.praefix.length) treffer = { praefix, label: eintrag.label };
  };
  for (const eintrag of NAV) {
    pruefe(eintrag);
    for (const kind of eintrag.kinder ?? []) pruefe(kind);
  }
  if (treffer) return (treffer as { label: string }).label;
  // Punkt 115 (G4): Vorgangsseiten haben keinen eigenen Menuepunkt; bis die Seite ihren Namen meldet
  // (`useSeitenKontextMelden`), steht wenigstens „Vorgang“ statt der Wortmarke da.
  return pfad.startsWith("/vorgaenge/") ? "Vorgang" : "MAESTRO";
}
